import React, { useEffect, useState } from "react";
import { Col, Form, Input, InputNumber, Row, Switch } from "antd";
import { toast } from "react-toastify";
import { ConfigurationModalProps, NpsConfigurationPayload } from "../../types";
import DateInput from "../dateInput";
import {
  ActionButtons,
  ActiveToggle,
  CancelButton,
  ConfirmButton,
  FooterActions,
  ModalForm,
  ModalWrapper,
  ToggleHint,
  ToggleLabel,
} from "./styles";

const ConfigurationModal: React.FC<ConfigurationModalProps> = ({
  open,
  configuration,
  onClose,
  onSubmit,
}) => {
  const [form] = Form.useForm<NpsConfigurationPayload>();
  const [isSaving, setIsSaving] = useState(false);
  const isEdit = configuration !== null;

  useEffect(() => {
    if (!open) return;

    if (configuration) {
      form.setFieldsValue({
        ...configuration,
        validFrom: configuration.validFrom.slice(0, 10),
        validTo: configuration.validTo.slice(0, 10),
      });
    } else {
      form.resetFields();
    }
  }, [open, configuration, form]);

  const handleSave = async () => {
    let values: NpsConfigurationPayload;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setIsSaving(true);
    try {
      await onSubmit({
        ...values,
        question: values.question.trim(),
        validTo: `${values.validTo}T23:59:59`,
      });
    } catch (error: any) {
      const detail = error?.response?.data;
      toast.error(typeof detail === "string" ? detail : "Erro ao salvar a pesquisa NPS.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalWrapper
      title={isEdit ? "Editar Pesquisa NPS" : "Nova Pesquisa NPS"}
      visible={open}
      onCancel={onClose}
      footer={null}
      centered
      width={1000}
      destroyOnClose
    >
      <ModalForm form={form} layout="vertical" initialValues={{ isActive: true }}>
        <Form.Item
          label="Pergunta"
          name="question"
          rules={[
            { required: true, whitespace: true, message: "A pergunta da pesquisa é obrigatória" },
            { max: 500, message: "O limite para a pergunta é de 500 caracteres" },
          ]}
        >
          <Input.TextArea
            rows={6}
            maxLength={500}
            showCount
            placeholder="Ex.: Em uma escala de 0 a 10, o quanto você recomendaria a nossa plataforma?"
          />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Início da campanha"
              name="validFrom"
              rules={[{ required: true, message: "A data de início da campanha é obrigatória" }]}
            >
              <DateInput placeholder="Data inicial" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Fim da campanha"
              name="validTo"
              dependencies={["validFrom"]}
              rules={[
                { required: true, message: "A data de fim da campanha é obrigatória" },
                ({ getFieldValue }) => ({
                  validator: (_, value) =>
                    !value || !getFieldValue("validFrom") || value > getFieldValue("validFrom")
                      ? Promise.resolve()
                      : Promise.reject(new Error("O fim da campanha deve ser posterior ao início")),
                }),
              ]}
            >
              <DateInput placeholder="Data final" />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <Form.Item
              label="Reexibir após (horas)"
              name="redisplayHours"
              extra="Horas até a pesquisa reaparecer para quem fechou sem responder."
              rules={[{ required: true, message: "A quantidade de horas para reexibição é obrigatória" }]}
            >
              <InputNumber
                min={1}
                precision={0}
                style={{ width: "100%" }}
                onKeyPress={(event) => !/[0-9]/.test(event.key) && event.preventDefault()}
              />
            </Form.Item>
          </Col>
        </Row>

        <FooterActions>
          <ActiveToggle>
            <Form.Item name="isActive" valuePropName="checked" noStyle>
              <Switch />
            </Form.Item>
            <span>
              <ToggleLabel>Campanha ativa</ToggleLabel>
              <ToggleHint>Apenas uma pesquisa fica ativa por vez.</ToggleHint>
            </span>
          </ActiveToggle>

          <ActionButtons>
            <CancelButton onClick={onClose}>Cancelar</CancelButton>
            <ConfirmButton onClick={handleSave} loading={isSaving}>
              {isEdit ? "Atualizar" : "Cadastrar"}
            </ConfirmButton>
          </ActionButtons>
        </FooterActions>
      </ModalForm>
    </ModalWrapper>
  );
};

export default ConfigurationModal;
